import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type {
  Lead,
  LeadCityTarget,
  LeadContactState,
  LeadPricingTier,
  LeadStage,
} from '../types'
import Badge, { type BadgeColor } from '../components/Badge'
import { fmtDate } from '../lib/format'
import LeadDetailView from '../components/marketing/LeadDetailView'

const STAGE_OPTIONS: LeadStage[] = [
  'new',
  'attempted',
  'contacted',
  'qualified',
  'consulted',
  'charrette',
  'proposal',
  'negotiating',
  'won',
  'lost',
  'won_to_royal_buyer',
]

const CONTACT_STATE_OPTIONS: LeadContactState[] = ['uncontacted', 'attempted', 'contacted']

const CITY_OPTIONS: LeadCityTarget[] = ['woodbury', 'afton', 'lake_elmo', 'stillwater', 'other']

const TIER_OPTIONS: LeadPricingTier[] = ['tier_1', 'tier_2', 'tier_3', 'tier_4', 'unknown']

const SOURCE_OPTIONS = [
  'website_form', 'manual', 'phone', 'agent_referral', 'past_client',
  'sub_referral', 'lender_referral', 'royal_team', 'lot_sale_outreach',
  'google_ads', 'linkedin_ads', 'instagram', 'gbp', 'houzz', 'organic',
  'direct', 'formspree', 'calendly', 'other',
]

function contactStateColor(s: LeadContactState): BadgeColor {
  if (s === 'contacted') return 'green'
  if (s === 'attempted') return 'gold'
  return 'red'
}

function stageColor(s: LeadStage): BadgeColor {
  if (s === 'won') return 'green'
  if (s === 'lost') return 'red'
  if (s === 'won_to_royal_buyer') return 'blue'
  return 'gray'
}

function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}

function minsAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
}

export default function LeadInboxTab() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)

  // Filters
  const [stageFilter, setStageFilter] = useState<LeadStage[]>([])
  const [sourceFilter, setSourceFilter] = useState<string[]>([])
  const [contactStateFilter, setContactStateFilter] = useState<LeadContactState[]>([])
  const [cityFilter, setCityFilter] = useState<LeadCityTarget[]>([])
  const [tierFilter, setTierFilter] = useState<LeadPricingTier[]>([])
  const [royalBuyerOnly, setRoyalBuyerOnly] = useState(false)
  const [royalListingOnly, setRoyalListingOnly] = useState(false)
  const [maxDaysSinceCreated, setMaxDaysSinceCreated] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.marketingLeads.list({
        stage: stageFilter.length ? stageFilter : undefined,
        source: sourceFilter.length ? sourceFilter : undefined,
        contact_state: contactStateFilter.length ? contactStateFilter : undefined,
        city_target: cityFilter.length ? cityFilter : undefined,
        pricing_tier_estimated: tierFilter.length ? tierFilter : undefined,
        royal_buyer_routed: royalBuyerOnly ? true : undefined,
        royal_listing_routed: royalListingOnly ? true : undefined,
        max_days_since_created: maxDaysSinceCreated
          ? parseInt(maxDaysSinceCreated, 10)
          : undefined,
        limit: 200,
      })
      setLeads(res.leads)
      setTotal(res.total_count)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stageFilter.join(','),
    sourceFilter.join(','),
    contactStateFilter.join(','),
    cityFilter.join(','),
    tierFilter.join(','),
    royalBuyerOnly,
    royalListingOnly,
    maxDaysSinceCreated,
  ])

  const speedToLeadCount = useMemo(() => {
    const fiveMinAgo = Date.now() - 5 * 60 * 1000
    return leads.filter(
      (l) => l.contact_state === 'uncontacted' && new Date(l.created_at).getTime() < fiveMinAgo
    ).length
  }, [leads])

  function clearFilters() {
    setStageFilter([])
    setSourceFilter([])
    setContactStateFilter([])
    setCityFilter([])
    setTierFilter([])
    setRoyalBuyerOnly(false)
    setRoyalListingOnly(false)
    setMaxDaysSinceCreated('')
  }

  if (selectedLeadId) {
    return (
      <LeadDetailView
        leadId={selectedLeadId}
        onBack={() => setSelectedLeadId(null)}
        onChanged={() => void refresh()}
      />
    )
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Lead Inbox</h2>
          <p className="muted small">
            Unified inbox for every Heritage lead. Royal buyer + listing tracks flag automatically.
            Speed-to-lead auto-text fires at 5 minutes uncontacted.
          </p>
        </div>
        <div className="row gap-2">
          <button className="btn-ghost small" onClick={() => setShowFilters((v) => !v)}>
            {showFilters ? 'Hide filters' : 'Filters'}
          </button>
          <button className="btn-primary small" onClick={() => setShowCreateModal(true)}>
            + New Lead
          </button>
        </div>
      </div>

      {speedToLeadCount > 0 && (
        <div
          className="card alert mb-2"
          style={{ background: '#fff3cd', borderLeft: '4px solid #d4af37' }}
        >
          <strong>Speed-to-lead alert</strong> — {speedToLeadCount} lead
          {speedToLeadCount === 1 ? '' : 's'} uncontacted past 5 minutes.
        </div>
      )}

      {error && <div className="card alert mb-2">{error}</div>}

      {showFilters && (
        <div className="card mb-2">
          <div className="row gap-3" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <FilterMulti
              label="Stage"
              options={STAGE_OPTIONS}
              value={stageFilter}
              onChange={(v) => setStageFilter(v as LeadStage[])}
            />
            <FilterMulti
              label="Contact state"
              options={CONTACT_STATE_OPTIONS}
              value={contactStateFilter}
              onChange={(v) => setContactStateFilter(v as LeadContactState[])}
            />
            <FilterMulti
              label="Source"
              options={SOURCE_OPTIONS}
              value={sourceFilter}
              onChange={setSourceFilter}
            />
            <FilterMulti
              label="City"
              options={CITY_OPTIONS}
              value={cityFilter}
              onChange={(v) => setCityFilter(v as LeadCityTarget[])}
            />
            <FilterMulti
              label="Pricing tier"
              options={TIER_OPTIONS}
              value={tierFilter}
              onChange={(v) => setTierFilter(v as LeadPricingTier[])}
            />
            <div>
              <div className="small muted mb-1">Royal flags</div>
              <label className="small">
                <input
                  type="checkbox"
                  checked={royalBuyerOnly}
                  onChange={(e) => setRoyalBuyerOnly(e.target.checked)}
                />{' '}
                Buyer routed
              </label>
              <br />
              <label className="small">
                <input
                  type="checkbox"
                  checked={royalListingOnly}
                  onChange={(e) => setRoyalListingOnly(e.target.checked)}
                />{' '}
                Listing routed
              </label>
            </div>
            <label className="small">
              <span className="muted">Max days since created</span>
              <br />
              <input
                style={{ width: 80 }}
                value={maxDaysSinceCreated}
                onChange={(e) => setMaxDaysSinceCreated(e.target.value)}
                placeholder="e.g. 30"
                inputMode="numeric"
              />
            </label>
          </div>
          <div className="row gap-2 mt-2">
            <button className="btn-ghost small" onClick={clearFilters}>
              Clear filters
            </button>
            <span className="muted small" style={{ alignSelf: 'center' }}>
              Showing {leads.length} of {total}
            </span>
          </div>
        </div>
      )}

      {loading && leads.length === 0 ? (
        <p className="muted">Loading…</p>
      ) : leads.length === 0 ? (
        <p className="muted">No leads match these filters.</p>
      ) : (
        leads.map((l) => {
          const minutesOld = minsAgo(l.created_at)
          const isStale = l.contact_state === 'uncontacted' && minutesOld >= 5
          return (
            <div
              key={l.id}
              className="card mb-2"
              style={{
                cursor: 'pointer',
                borderLeft: isStale
                  ? '4px solid #d4af37'
                  : l.contact_state === 'contacted'
                  ? '4px solid #2ea043'
                  : '4px solid transparent',
              }}
              onClick={() => setSelectedLeadId(l.id)}
            >
              <div className="row-between">
                <div style={{ flex: 1 }}>
                  <div className="row-title">
                    {l.name ?? '(no name)'}
                    {l.pricing_tier_estimated !== 'unknown' && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: '0.75rem',
                          color: '#666',
                        }}
                      >
                        {l.pricing_tier_estimated}
                      </span>
                    )}
                  </div>
                  <div className="muted small mt-1">
                    {l.email ?? '—'} · {l.phone ?? '—'} ·{' '}
                    {minutesOld < 60
                      ? `${minutesOld}m ago`
                      : daysAgo(l.created_at) === 0
                      ? `${Math.floor(minutesOld / 60)}h ago`
                      : `${daysAgo(l.created_at)}d ago`}{' '}
                    · {l.source}
                  </div>
                  <div className="row gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
                    <Badge color={stageColor(l.stage)}>{l.stage}</Badge>
                    <Badge color={contactStateColor(l.contact_state)}>{l.contact_state}</Badge>
                    {l.city_target && <Badge color="gray">{l.city_target}</Badge>}
                    {l.royal_buyer_routed === 1 && <Badge color="blue">Royal Buyer</Badge>}
                    {l.royal_listing_routed === 1 && <Badge color="blue">Royal Listing</Badge>}
                    {l.assigned_to && <Badge color="gray">→ {l.assigned_to}</Badge>}
                  </div>
                </div>
                <div className="muted small" style={{ textAlign: 'right' }}>
                  {fmtDate(l.created_at)}
                </div>
              </div>
            </div>
          )
        })
      )}

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onSaved={(id) => {
            setShowCreateModal(false)
            void refresh()
            setSelectedLeadId(id)
          }}
        />
      )}
    </section>
  )
}

function FilterMulti({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly string[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  function toggle(opt: string) {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt))
    else onChange([...value, opt])
  }
  return (
    <div>
      <div className="small muted mb-1">{label}</div>
      {options.map((o) => (
        <label key={o} className="small" style={{ display: 'block' }}>
          <input
            type="checkbox"
            checked={value.includes(o)}
            onChange={() => toggle(o)}
          />{' '}
          {o}
        </label>
      ))}
    </div>
  )
}

function CreateLeadModal({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: (id: string) => void
}) {
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [source, setSource] = useState('manual')
  const [lotStatus, setLotStatus] = useState<string>('')
  const [existingHome, setExistingHome] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [budget, setBudget] = useState('')
  const [timeline, setTimeline] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!email && !phone) {
      setError('Email or phone required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const r = await api.marketingLeads.create({
        first_name: first || null,
        last_name: last || null,
        email: email || null,
        phone: phone || null,
        source,
        lot_status: lotStatus || null,
        existing_home_status: existingHome || null,
        city_target: city || null,
        budget_range: budget || null,
        timeline: timeline || null,
        message: message || null,
      })
      onSaved(r.id)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <h3>New Lead</h3>
        <p className="muted small mb-3">
          Manual entry. The website form posts to /api/public/leads instead.
        </p>
        {error && <div className="card alert mb-2">{error}</div>}
        <div className="row gap-2">
          <label className="form-row" style={{ flex: 1 }}>
            <span>First name</span>
            <input value={first} onChange={(e) => setFirst(e.target.value)} />
          </label>
          <label className="form-row" style={{ flex: 1 }}>
            <span>Last name</span>
            <input value={last} onChange={(e) => setLast(e.target.value)} />
          </label>
        </div>
        <label className="form-row">
          <span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
        </label>
        <label className="form-row">
          <span>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" />
        </label>
        <label className="form-row">
          <span>Source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="form-row">
          <span>Lot status</span>
          <select value={lotStatus} onChange={(e) => setLotStatus(e.target.value)}>
            <option value="">—</option>
            <option value="own">I own the lot</option>
            <option value="under_contract">Lot under contract</option>
            <option value="looking">Still looking</option>
            <option value="heritage_helps">Heritage to help find</option>
          </select>
        </label>
        <label className="form-row">
          <span>Existing home</span>
          <select value={existingHome} onChange={(e) => setExistingHome(e.target.value)}>
            <option value="">—</option>
            <option value="selling">Selling existing</option>
            <option value="keeping">Keeping</option>
            <option value="relocating">Relocating</option>
            <option value="rents">Currently renting</option>
          </select>
        </label>
        <label className="form-row">
          <span>City target</span>
          <select value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">—</option>
            {CITY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="form-row">
          <span>Budget range</span>
          <input
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="e.g. $1.5M-2M"
          />
        </label>
        <label className="form-row">
          <span>Timeline</span>
          <input
            value={timeline}
            onChange={(e) => setTimeline(e.target.value)}
            placeholder="e.g. start in 3-6 months"
          />
        </label>
        <label className="form-row">
          <span>Message</span>
          <textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        <div className="row gap-2 mt-3">
          <button className="btn-primary small" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Create lead'}
          </button>
          <button className="btn-ghost small" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
